import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ItemSupplier } from '../../types';
import {
  BasicInfoSection,
  ProductCodesSection,
  PackagingSection,
  PricingSection,
  type FormState,
} from './AddSupplierFormSections';

interface AddSupplierModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (supplier: Omit<ItemSupplier, 'supplierId'>) => void;
}

const INITIAL_FORM: FormState = {
  supplierName: '',
  supplierSku: '',
  supplierProductName: '',
  ean: '',
  upc: '',
  dun14: '',
  gtin: '',
  unitPrice: '',
  casePrice: '',
  currency: 'USD',
  leadTimeDays: '',
  minOrderQuantity: '1',
  orderMultiple: '',
  maxOrderQuantity: '',
  packagingUnitSize: '',
  packagingUnitOfMeasure: '',
  packagingUnitsPerCase: '',
  packagingCasesPerPallet: '',
  packagingDescription: '',
  isPreferred: false,
  notes: '',
};

type SectionId = 'basic' | 'codes' | 'packaging' | 'pricing';

const SECTIONS: Array<{
  id: SectionId;
  label: string 
}> = [
  {
    id: 'basic',
    label: 'Basic Info' 
  },
  {
    id: 'codes',
    label: 'Product Codes' 
  },
  {
    id: 'packaging',
    label: 'Packaging' 
  },
  {
    id: 'pricing',
    label: 'Pricing & Terms' 
  },
];

interface ModalHeaderProps {readonly onClose: () => void;}

function ModalHeader({ onClose }: ModalHeaderProps) {
  return (
    <div className="flex items-center justify-between border-b p-4">
      <h2 className="text-lg font-semibold text-gray-900">Add Supplier</h2>
      <button
        type="button"
        onClick={onClose}
        className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

interface SectionTabsProps {
  readonly activeSection: SectionId;
  readonly onSectionChange: (section: SectionId) => void;
}

function SectionTabs({
  activeSection, onSectionChange 
}: SectionTabsProps) {
  const getTabClass = (sectionId: SectionId) => {
    const base = 'border-b-2 py-3 text-sm font-medium transition-colors';
    if (activeSection === sectionId) {
      return base + ' border-primary text-primary';
    }
    return base + ' border-transparent text-gray-500 hover:text-gray-700';
  };

  return (
    <div className="border-b px-4">
      <nav className="flex gap-4">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => onSectionChange(section.id)}
            className={getTabClass(section.id)}
          >
            {section.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

interface ModalFooterProps {
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly isValid: boolean;
}

function ModalFooter({
  onClose, onSave, isValid 
}: ModalFooterProps) {
  return (
    <div className="flex items-center justify-end gap-3 border-t p-4">
      <Button variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button onClick={onSave} disabled={!isValid}>
        Add Supplier
      </Button>
    </div>
  );
}

export function AddSupplierModal({
  isOpen,
  onClose,
  onSave,
}: AddSupplierModalProps) {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [activeSection, setActiveSection] = useState<SectionId>('basic');

  if (!isOpen) return null;

  const updateField = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({
      ...prev,
      [field]: value 
    }));
  };

  const buildSupplier = (): Omit<ItemSupplier, 'supplierId'> => {
    const packaging = form.packagingUnitSize
      ? {
        unitSize: parseFloat(form.packagingUnitSize),
        unitOfMeasure: form.packagingUnitOfMeasure,
        unitsPerCase: form.packagingUnitsPerCase
          ? parseInt(form.packagingUnitsPerCase, 10)
          : undefined,
        casesPerPallet: form.packagingCasesPerPallet
          ? parseInt(form.packagingCasesPerPallet, 10)
          : undefined,
        description: form.packagingDescription || undefined,
      }
      : undefined;

    return {
      supplierName: form.supplierName,
      supplierSku: form.supplierSku || undefined,
      supplierProductName: form.supplierProductName || undefined,
      ean: form.ean || undefined,
      upc: form.upc || undefined,
      dun14: form.dun14 || undefined,
      gtin: form.gtin || undefined,
      unitPrice: parseFloat(form.unitPrice),
      casePrice: form.casePrice ? parseFloat(form.casePrice) : undefined,
      currency: form.currency,
      leadTimeDays: parseInt(form.leadTimeDays, 10),
      minOrderQuantity: parseInt(form.minOrderQuantity, 10) || 1,
      orderMultiple: form.orderMultiple ? parseInt(form.orderMultiple, 10) : undefined,
      maxOrderQuantity: form.maxOrderQuantity ? parseInt(form.maxOrderQuantity, 10) : undefined,
      packaging,
      isPreferred: form.isPreferred,
      notes: form.notes || undefined,
    };
  };

  const handleSave = () => {
    if (!form.supplierName || !form.unitPrice || !form.leadTimeDays) return;
    onSave(buildSupplier());
    setForm(INITIAL_FORM);
    onClose();
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    onClose();
  };

  const isFormValid = form.supplierName && form.unitPrice && form.leadTimeDays;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <ModalHeader onClose={handleClose} />
        <SectionTabs activeSection={activeSection} onSectionChange={setActiveSection} />
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {activeSection === 'basic' && (
            <BasicInfoSection form={form} updateField={updateField} />
          )}
          {activeSection === 'codes' && (
            <ProductCodesSection form={form} updateField={updateField} />
          )}
          {activeSection === 'packaging' && (
            <PackagingSection form={form} updateField={updateField} />
          )}
          {activeSection === 'pricing' && (
            <PricingSection form={form} updateField={updateField} />
          )}
        </div>
        <ModalFooter onClose={handleClose} onSave={handleSave} isValid={Boolean(isFormValid)} />
      </div>
    </div>
  );
}
